const DAY_ORDER = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
};

function normalizeText(value) {
  return String(value || '').trim();
}

function inferDayName(value) {
  const text = normalizeText(value);
  const match = text.toLowerCase().match(/monday|tuesday|wednesday|thursday|friday|saturday|sunday/);
  if (!match) return text;
  return match[0].charAt(0).toUpperCase() + match[0].slice(1);
}

function weekOrder(value) {
  const text = normalizeText(value).toLowerCase();
  const match = text.match(/week\s*(\d+)/);
  return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
}

function dayOrder(value) {
  const text = normalizeText(value).toLowerCase();
  return DAY_ORDER[text] || Number.MAX_SAFE_INTEGER;
}

function extractJsonText(input) {
  const raw = String(input || '').trim();
  if (!raw) return raw;

  const fenced = raw.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fenced) return fenced[1].trim();

  return raw;
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isCurriculumEntryLike(value) {
  return isPlainObject(value) && (
    value.topic != null
    || value.objective != null
    || value.learningActivities != null
    || value.goals != null
    || value.dayName != null
    || value.day != null
    || value.dayLabel != null
  );
}

function collectEntriesFromValue(value, weekLabel, weeklyObjective) {
  if (Array.isArray(value)) {
    return value.flatMap((item) => collectEntriesFromValue(item, weekLabel, weeklyObjective));
  }

  if (!isPlainObject(value)) {
    return [];
  }

  if (isCurriculumEntryLike(value)) {
    return [normalizeCurriculumEntry({
      ...value,
      weekLabel: value.weekLabel || weekLabel,
      week: value.week || weekLabel,
      weeklyObjective: value.weeklyObjective || weeklyObjective,
    })];
  }

  return [];
}

function pickFirstNonEmptyArray(...candidates) {
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length) {
      return candidate;
    }
  }
  return null;
}

function normalizeWeekBlockEntries(block = {}) {
  const weekLabel = normalizeText(block.weekLabel || block.week || block.title || '');
  const weeklyObjective = normalizeText(block.weeklyObjective || block.weekObjective || block.summary || block.summaryObjective || '');
  const sourceEntries = [
    ...(Array.isArray(block.entries) ? block.entries : []),
    ...(Array.isArray(block.days) ? block.days : []),
    ...(Array.isArray(block.rows) ? block.rows : []),
    ...(Array.isArray(block.blocks) ? block.blocks : []),
    ...(Array.isArray(block.dailyRows) ? block.dailyRows : []),
  ];

  const weekdayEntries = Object.entries(block)
    .filter(([key, value]) => WEEKDAY_KEYS.includes(key.toLowerCase()) && value != null)
    .flatMap(([key, value]) => {
      const dayName = inferDayName(key);
      return collectEntriesFromValue(value, weekLabel, weeklyObjective).map((entry) => ({
        ...entry,
        weekLabel: entry.weekLabel || weekLabel,
        week: entry.week || weekLabel,
        dayName: entry.dayName || dayName,
        day: entry.day || dayName,
        dayLabel: entry.dayLabel || `${weekLabel} - ${dayName}`,
        weeklyObjective: entry.weeklyObjective || weeklyObjective,
      }));
    });

  const collectedEntries = [
    ...sourceEntries.flatMap((entry) => collectEntriesFromValue(entry, weekLabel, weeklyObjective)),
    ...weekdayEntries,
  ];

  if (!collectedEntries.length && isCurriculumEntryLike(block)) {
    return [normalizeCurriculumEntry({
      ...block,
      weekLabel: block.weekLabel || weekLabel,
      week: block.week || weekLabel,
      weeklyObjective: block.weeklyObjective || weeklyObjective,
    })];
  }

  return collectedEntries;
}

const WEEKDAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

export function normalizeCurriculumEntry(entry = {}) {
  const weekLabel = normalizeText(entry.weekLabel || entry.week || 'Week 1') || 'Week 1';
  const dayName = inferDayName(entry.dayName || entry.dayLabel || entry.day || '');
  const dayLabel = normalizeText(entry.dayLabel || (dayName ? `${weekLabel} - ${dayName}` : weekLabel));

  return {
    weekLabel,
    weekOrder: Number.isFinite(entry.weekOrder) ? entry.weekOrder : weekOrder(weekLabel),
    dayName,
    dayOrder: Number.isFinite(entry.dayOrder) ? entry.dayOrder : dayOrder(dayName),
    dayLabel,
    topic: normalizeText(entry.topic),
    objective: normalizeText(entry.objective),
    learningActivities: normalizeText(entry.learningActivities),
    goals: normalizeText(entry.goals),
    weeklyObjective: normalizeText(entry.weeklyObjective),
  };
}

function getWeeklyObjective(entries = []) {
  const match = entries.find((entry) => normalizeText(entry.weeklyObjective));
  return normalizeText(match?.weeklyObjective);
}

export function groupCurriculumEntries(entries = []) {
  const normalized = entries.map(normalizeCurriculumEntry).sort((a, b) => a.weekOrder - b.weekOrder || a.dayOrder - b.dayOrder || a.dayLabel.localeCompare(b.dayLabel));
  const groups = new Map();

  normalized.forEach((entry) => {
    const key = entry.weekLabel;
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(entry);
  });

  return Array.from(groups.entries()).map(([weekLabel, weekEntries]) => ({
    weekLabel,
    weekOrder: weekEntries[0]?.weekOrder ?? Number.MAX_SAFE_INTEGER,
    weeklyObjective: getWeeklyObjective(weekEntries),
    entries: weekEntries,
  })).sort((a, b) => a.weekOrder - b.weekOrder || a.weekLabel.localeCompare(b.weekLabel));
}

export function buildCurriculumJson(course, tenant, curriculumEntries = []) {
  const normalizedEntries = curriculumEntries.map(normalizeCurriculumEntry);
  const weekBlocks = groupCurriculumEntries(normalizedEntries);

  return {
    version: 1,
    exportedAt: new Date().toISOString(),
    academy: {
      name: tenant?.name || '',
      slug: tenant?.slug || '',
      type: tenant?.type || '',
      timezone: tenant?.settings?.timezone || '',
      currency: tenant?.settings?.currency || '',
    },
    courseName: course?.name || '',
    courseCode: course?.code || '',
    courseDescription: course?.description || '',
    duration: course?.duration || '',
    organization: tenant?.name || '',
    course: {
      id: course?.id || '',
      name: course?.name || '',
      code: course?.code || '',
      description: course?.description || '',
      duration: course?.duration || '',
      feeAmount: Number(course?.feeAmount || 0),
      status: course?.status || '',
      instructorId: course?.instructorId || '',
      instructorName: course?.instructorName || '',
      publicEnrollment: {
        enabled: Boolean(course?.publicEnrollment?.enabled),
        intakeMode: course?.publicEnrollment?.intakeMode || 'open',
        signupMessage: course?.publicEnrollment?.signupMessage || '',
      },
    },
    schemeOfWork: {
      summary: {
        totalWeeks: weekBlocks.length,
        totalBlocks: normalizedEntries.length,
      },
      weekBlocks: weekBlocks.map((week) => ({
        weekLabel: week.weekLabel,
        weekOrder: week.weekOrder,
        weeklyObjective: week.weeklyObjective,
        days: week.entries,
      })),
    },
    curriculumEntries: normalizedEntries,
  };
}

export function buildCurriculumPrompt(course, tenant, curriculumEntries = [], promptValues = {}) {
  const normalizedEntries = curriculumEntries.map(normalizeCurriculumEntry);
  const weekBlocks = groupCurriculumEntries(normalizedEntries);
  const targetLearners = normalizeText(promptValues.targetLearners);
  const focusAreas = normalizeText(promptValues.focusAreas);
  const extraInstructions = normalizeText(promptValues.extraInstructions);

  return [
    'Generate JSON for a course scheme of work that will be pasted into an app.',
    'Return JSON only. Do not include markdown, bullet points, explanations, or code fences.',
    'The JSON must match a table workflow where each week contains five daily rows for Monday through Friday.',
    '',
    'Required shape:',
    '{',
    '  "version": 1,',
    '  "exportedAt": "ISO timestamp",',
    '  "academy": { "name": "", "slug": "", "type": "", "timezone": "", "currency": "" },',
    '  "courseName": "",',
    '  "courseCode": "",',
    '  "courseDescription": "",',
    '  "duration": "",',
    '  "organization": "",',
    '  "course": {',
    '    "id": "",',
    '    "name": "",',
    '    "code": "",',
    '    "description": "",',
    '    "duration": "",',
    '    "feeAmount": 0,',
    '    "status": "",',
    '    "instructorId": "",',
    '    "instructorName": "",',
    '    "publicEnrollment": { "enabled": true, "intakeMode": "open", "signupMessage": "" }',
    '  },',
    '  "schemeOfWork": {',
    '    "summary": { "totalWeeks": 0, "totalBlocks": 0 },',
    '    "weekBlocks": [',
    '      {',
    '        "weekLabel": "Week 1",',
    '        "weekOrder": 1,',
    '        "weeklyObjective": "",',
    '        "days": [',
    '          {',
    '            "weekLabel": "Week 1",',
    '            "dayName": "Monday",',
    '            "dayLabel": "Week 1 - Monday",',
    '            "topic": "",',
    '            "objective": "",',
    '            "learningActivities": "",',
    '            "goals": "",',
    '            "weeklyObjective": ""',
    '          }',
    '        ]',
    '      }',
    '    ]',
    '  },',
    '  "curriculumEntries": []',
    '}',
    '',
    'Course details to include in the content:',
    `- Course name: ${course?.name || ''}`,
    `- Course code: ${course?.code || ''}`,
    `- Course description: ${course?.description || ''}`,
    `- Duration: ${course?.duration || ''}`,
    `- Fee: ${Number(course?.feeAmount || 0)}`,
    `- Status: ${course?.status || ''}`,
    `- Instructor: ${course?.instructorName || ''}`,
    `- Academy: ${tenant?.name || ''}`,
    `- Academy type: ${tenant?.type || ''}`,
    `- Weeks already in the scheme of work: ${weekBlocks.length}`,
    `- Existing blocks: ${normalizedEntries.length}`,
    targetLearners ? `- Target learners: ${targetLearners}` : '',
    focusAreas ? `- Focus areas: ${focusAreas}` : '',
    extraInstructions ? `- Extra instructions: ${extraInstructions}` : '',
    '',
    'Rules:',
    '- Create a separate week block for each week.',
    '- Inside every week block, include exactly five daily rows in this order: Monday, Tuesday, Wednesday, Thursday, Friday.',
    '- Do not skip any weekday rows, even if the content is brief.',
    '- Every daily row must include weekLabel, dayName, dayLabel, topic, objective, learningActivities, and goals.',
    '- Every week block must also include a weeklyObjective summary.',
    '- Keep the wording practical, detailed, and ready for teaching.',
    '- Preserve the course description and other course metadata.',
    '- Return valid JSON that can be pasted back into the app and edited row by row.',
  ].filter(Boolean).join('\n');
}

export function parseCurriculumJson(input) {
  const raw = extractJsonText(input);
  let parsed;

  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    const objectStart = raw.indexOf('{');
    const objectEnd = raw.lastIndexOf('}');
    const arrayStart = raw.indexOf('[');
    const arrayEnd = raw.lastIndexOf(']');

    if (objectStart !== -1 && objectEnd !== -1 && objectEnd > objectStart) {
      parsed = JSON.parse(raw.slice(objectStart, objectEnd + 1));
    } else if (arrayStart !== -1 && arrayEnd !== -1 && arrayEnd > arrayStart) {
      parsed = JSON.parse(raw.slice(arrayStart, arrayEnd + 1));
    } else {
      throw error;
    }
  }

  const sourceEntries = Array.isArray(parsed)
    ? parsed
    : pickFirstNonEmptyArray(
      parsed?.curriculumEntries,
      parsed?.schemeOfWork?.curriculumEntries,
      Array.isArray(parsed?.schemeOfWork?.weekBlocks)
        ? parsed.schemeOfWork.weekBlocks.flatMap(normalizeWeekBlockEntries)
        : isPlainObject(parsed?.schemeOfWork?.weekBlocks)
          ? Object.values(parsed.schemeOfWork.weekBlocks).flatMap(normalizeWeekBlockEntries)
          : null,
      Array.isArray(parsed?.weekBlocks)
        ? parsed.weekBlocks.flatMap(normalizeWeekBlockEntries)
        : isPlainObject(parsed?.weekBlocks)
          ? Object.values(parsed.weekBlocks).flatMap(normalizeWeekBlockEntries)
          : null,
      Array.isArray(parsed?.weeks)
        ? parsed.weeks.flatMap(normalizeWeekBlockEntries)
        : isPlainObject(parsed?.weeks)
          ? Object.values(parsed.weeks).flatMap(normalizeWeekBlockEntries)
          : null,
    );

  if (!Array.isArray(sourceEntries)) {
    throw new Error('Expected a JSON object with a curriculumEntries array or a top-level array.');
  }

  return sourceEntries.map((entry) => normalizeCurriculumEntry(entry));
}
